import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Truck, Mail, Phone, Zap } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";
const SUPPORT_EMAIL = "contact@misyou.in";
const SUPPORT_PHONE = "+91 8146499739";
const EFFECTIVE_DATE = "22 February 2026";

export default function ShippingDeliveryPolicyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="MISYOU" className="w-8 h-8 rounded-lg object-cover" />
              <span className="font-bold text-slate-900">MISYOU</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24">
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Truck className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Shipping &amp; Delivery Policy</h1>
          <p className="text-muted-foreground">Effective Date: {EFFECTIVE_DATE}</p>
        </div>

        <Card className="shadow-lg border-slate-100">
          <CardContent className="p-6 sm:p-10 prose prose-slate max-w-none">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4 flex items-start gap-3">
              <Zap className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
              <p className="text-blue-900 mb-0">
                <strong>MISYOU is a 100% digital service.</strong> We do not sell or ship any
                physical goods. All services are delivered electronically over the internet.
              </p>
            </div>

            <h2>1. Nature of Service</h2>
            <p>
              MISYOU is a Software-as-a-Service (SaaS) platform that helps families and communities
              report and locate missing persons. All products and services offered by MISYOU
              &mdash; including but not limited to case listings, subscription plans, sighting
              submissions, and access to community reach &mdash; are entirely digital and are
              delivered electronically through our website (<strong>misyou.in</strong>) and
              mobile application.
            </p>
            <p>
              <strong>We do not ship any physical goods.</strong> Consequently, no shipping,
              handling, courier, or postal charges apply to any MISYOU transaction.
            </p>

            <h2>2. Digital Delivery Timeline</h2>
            <p>
              Because MISYOU is entirely digital, "delivery" refers to the electronic activation of
              the service you purchased.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-slate-200 rounded-lg my-3">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2 border-b border-slate-200">Product</th>
                    <th className="text-left p-2 border-b border-slate-200">Delivery method</th>
                    <th className="text-left p-2 border-b border-slate-200">Timeline</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100">
                    <td className="p-2">Standard Subscription (₹20/day)</td>
                    <td className="p-2">Instant activation on your MISYOU account</td>
                    <td className="p-2"><strong>Within 2 minutes</strong> of payment confirmation from the gateway</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="p-2">Case listing publication</td>
                    <td className="p-2">Automatic publication after moderation</td>
                    <td className="p-2">Immediate (subject to standard moderation review, usually &lt; 15 minutes)</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="p-2">Invoice / receipt</td>
                    <td className="p-2">Emailed to your registered address; downloadable from Dashboard &rarr; My Subscriptions</td>
                    <td className="p-2">Within 15 minutes of successful payment</td>
                  </tr>
                  <tr>
                    <td className="p-2">Evidence Pack (police-friendly ZIP)</td>
                    <td className="p-2">On-demand download from the case detail page</td>
                    <td className="p-2">Generated in real time (typically &lt; 30 seconds)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2>3. Confirmation of Delivery</h2>
            <p>
              Upon successful payment, you will receive:
            </p>
            <ul>
              <li>An <strong>on-screen confirmation</strong> that your subscription is active.</li>
              <li>An <strong>email receipt / invoice</strong> to the address registered with your MISYOU account.</li>
              <li>Immediate updates on your <strong>Dashboard</strong> and the affected case listing(s).</li>
            </ul>
            <p>
              If any of the above does not happen within <strong>15 minutes</strong> of your payment,
              please refer to Section 4 below.
            </p>

            <h2>4. What to Do if Your Service is Not Activated</h2>
            <p>
              If your payment has been debited but the service has not been activated within 15
              minutes:
            </p>
            <ol>
              <li>Refresh the Subscription page and check the "My Subscriptions" section on your Dashboard.</li>
              <li>
                Contact us at{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary font-medium">{SUPPORT_EMAIL}</a>{' '}
                with your registered email, order ID / transaction reference, and the payment date.
              </li>
              <li>We will trace the transaction and either manually activate your service or process a full refund within 2 business days.</li>
            </ol>

            <h2>5. Failed or Delayed Deliveries</h2>
            <p>
              In the unlikely event that we are unable to deliver the digital service you paid for
              (e.g., because of an unresolved technical fault on our side), we will:
            </p>
            <ul>
              <li>Credit your account with equivalent additional subscription days at no charge, OR</li>
              <li>Provide a full refund of the affected payment (at your option).</li>
            </ul>
            <p>
              Refund timelines and process are described in our{' '}
              <Link to="/refund" className="underline">Refund &amp; Cancellation Policy</Link>.
            </p>

            <h2>6. Geographical Availability</h2>
            <p>
              MISYOU is currently offered only to users located in <strong>India</strong>. All
              digital services can be accessed 24&times;7 from anywhere in India where a working
              internet connection is available. There is no physical or geographic restriction on
              digital "delivery" other than lawful access via the internet.
            </p>

            <h2>7. Contact Us</h2>
            <p>For any question about digital delivery or activation of your MISYOU service:</p>
            <ul>
              <li>
                <Mail className="inline w-4 h-4 mr-1 -mt-0.5" />
                Email: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary font-medium">{SUPPORT_EMAIL}</a>
              </li>
              <li>
                <Phone className="inline w-4 h-4 mr-1 -mt-0.5" />
                Customer Care: <a href="tel:+918146499739" className="text-primary font-medium">{SUPPORT_PHONE}</a> (Mon&ndash;Sat, 10am&ndash;7pm IST)
              </li>
              <li>Business name: <strong>MISYOU</strong></li>
              <li>Legal entity: Individually owned by Dinesh Bansal</li>
              <li>
                Registered address: St. No.-05, Natt Road, SBS Nagar,
                Talwandi Sabo, Bathinda, Punjab &ndash; 151302, India
              </li>
            </ul>

            <h2>8. Changes to this Policy</h2>
            <p>
              We may update this Shipping &amp; Delivery Policy from time to time. Material changes
              will be notified to registered users by email or in-app notification. Continued use of
              MISYOU after such changes constitutes acceptance of the revised policy.
            </p>
          </CardContent>
        </Card>

        <div className="text-center mt-10">
          <p className="text-sm text-muted-foreground">
            MISYOU is a 100% digital service &mdash; no physical goods are shipped.
          </p>
        </div>
      </main>
    </div>
  );
}
